// Mappers between the Withdrawal Rules V2 toggle-based UI state and
// the versioned decision-graph body accepted by the BE.
//
// The toggle UI is an opinionated wrapper around the general-purpose
// DAG the BE stores. This module is the *only* place where those two
// worlds meet — every other consumer either edits toggles (RulesTab)
// or renders graphs (DecisionGraph). Keeping the translation isolated
// here makes it testable in isolation and gives us one place to
// change when the BE feature vocabulary evolves.
//
// Two functions, both pure:
//
//   forward:  toggleStateToGraph(state) → SerializeResult
//   reverse:  graphToToggleState(graph) → DeserializeResult
//
// Forward is many-to-one: the same graph can be produced from
// slightly different toggle states (e.g. an OR of one condition
// collapses to just the condition). Reverse is best-effort: any
// graph the BE returns that doesn't match the canonical forward
// output is refused with `ok: false` — the UI then falls back to
// read-only mode with a "Advanced graph" banner (documented in the
// PAM v2 Withdrawal Rules design doc).
//
// Structural convention (matches DecisionGraph fixture + spec):
//
//   t1_src (source, tier_1)          "always emit both tiers"
//     └─▶ t1_decision (decision)
//           ├─ edge(cond=OR of active Tier-1 rules) ─▶ t2_src
//           └─ default_target ─────────────────────▶ processed
//   t2_src (source, tier_2)
//     └─▶ t2_decision (decision)
//           ├─ edge(cond=OR of active Tier-2 rules) ─▶ auto_approve
//           └─ default_target ─────────────────────▶ human_review
//   processed, auto_approve, human_review (terminals)
//
// The `processed` terminal is intentionally distinct from
// `auto_approve` even though both let the withdrawal through
// unchanged at runtime. It labels the "T1 didn't fire" fallthrough
// separately from the "T2 rule cleared it" active approval — see
// TerminalDecision in `./types.ts` for the analytics rationale.
//
// Rationale (see the "Sobre a serialização" note in the design chat):
//  - Always emit both tiers, even when empty. Keeps the mapper
//    branch-free and produces stable diffs between versions.
//  - Deterministic IDs. Same toggle state ⇒ same graph JSON,
//    byte-for-byte. Helps auditability and version diffing.

import type {
  ConditionNode,
  DagNode,
  DecisionEdge,
  DecisionNode,
  FeatureRefNode,
  LiteralNode,
  OperatorNode,
  PublishRuleGraphBody,
  RuleGraph,
  SourceNode,
  TerminalNode,
} from "./types";

// ─── Rule identifiers (mirror `RulesTab.jsx`) ───────────────────────

/**
 * Tier-1 rule ids. These are the string keys the RulesTab component
 * uses in its `useState` bag, so the mapper is coupled to the UI
 * only through this constant. A rename in RulesTab requires a
 * rename here — enforced by the compile-time keys of the
 * `Tier1RuleState` type below.
 */
export const TIER_1_RULE_IDS = {
  MAX_AMOUNT: "maximum_transaction_amount",
  FIRST_TIME_WITHDRAWAL: "first_time_withdrawal",
  WITHDRAWAL_PER_DAY: "withdrawal_per_day_for_x_days",
  WITHDRAWALS_SINCE_DEPOSIT: "withdrawals_since_last_deposit",
  X_TIMES_LAST_DEPOSIT: "withdrawals_x_times_last_deposit",
} as const;

export const TIER_2_RULE_IDS = {
  REVIEWED_X_TIMES: "reviewed_x_times_within_y",
  VIP_MATCHING: "vip_with_matching_instrument",
  SAME_INSTRUMENT_X_TIMES: "same_instrument_used_x_times_within_y",
  MIN_ACCOUNT_AGE: "minimum_account_age",
  DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK: "deposit_payment_instrument_lookback",
} as const;

export type Tier1RuleId =
  (typeof TIER_1_RULE_IDS)[keyof typeof TIER_1_RULE_IDS];
export type Tier2RuleId =
  (typeof TIER_2_RULE_IDS)[keyof typeof TIER_2_RULE_IDS];

/**
 * The Select values RulesTab renders in the period dropdown. Only
 * `HOUR | DAY | WEEK` map to a BE-supported window suffix per the
 * spec's `_<n><h|d|w>` convention — the UI's `MONTH` / `YEAR`
 * options are surfaced by the shared `PERIOD_OPTIONS` constant but
 * are *not* valid for windowed features (BE will 422). The mapper
 * rejects them at serialize time with a clear error so the caller
 * can surface a validation message before the POST.
 */
export type PeriodValue = "HOUR" | "DAY" | "WEEK" | "MONTH" | "YEAR";

const PERIOD_TO_SUFFIX: Partial<Record<PeriodValue, "h" | "d" | "w">> = {
  HOUR: "h",
  DAY: "d",
  WEEK: "w",
};

const SPEI_PAYMENT_INSTRUMENT = "spei";

// ─── Toggle state shapes (per rule) ─────────────────────────────────

export interface MaxAmountState {
  readonly enabled: boolean;
  readonly amount: string;
}
export interface FirstTimeWithdrawalState {
  readonly enabled: boolean;
}
export interface WithdrawalPerDayState {
  readonly enabled: boolean;
  readonly threshold: string;
  readonly days: string;
}
export interface WithdrawalsSinceDepositState {
  readonly enabled: boolean;
  readonly withdrawals: string;
}
export interface XTimesLastDepositState {
  readonly enabled: boolean;
  readonly multiplier: string;
}

export interface Tier1RuleState {
  readonly [TIER_1_RULE_IDS.MAX_AMOUNT]: MaxAmountState;
  readonly [TIER_1_RULE_IDS.FIRST_TIME_WITHDRAWAL]: FirstTimeWithdrawalState;
  readonly [TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY]: WithdrawalPerDayState;
  readonly [TIER_1_RULE_IDS
    .WITHDRAWALS_SINCE_DEPOSIT]: WithdrawalsSinceDepositState;
  readonly [TIER_1_RULE_IDS.X_TIMES_LAST_DEPOSIT]: XTimesLastDepositState;
}

export interface ReviewedXTimesState {
  readonly enabled: boolean;
  readonly reviews: string;
  readonly within: string;
  readonly period: PeriodValue;
}
export interface VipMatchingState {
  readonly enabled: boolean;
  readonly within: string;
  readonly period: PeriodValue;
}
export interface SameInstrumentXTimesState {
  readonly enabled: boolean;
  readonly uses: string;
  readonly within: string;
  readonly period: PeriodValue;
}
export interface MinAccountAgeState {
  readonly enabled: boolean;
  readonly days: string;
}
export interface DepositPaymentInstrumentLookbackState {
  readonly enabled: boolean;
  readonly lookback: string;
}

export interface Tier2RuleState {
  readonly [TIER_2_RULE_IDS.REVIEWED_X_TIMES]: ReviewedXTimesState;
  readonly [TIER_2_RULE_IDS.VIP_MATCHING]: VipMatchingState;
  readonly [TIER_2_RULE_IDS
    .SAME_INSTRUMENT_X_TIMES]: SameInstrumentXTimesState;
  readonly [TIER_2_RULE_IDS.MIN_ACCOUNT_AGE]: MinAccountAgeState;
  readonly [TIER_2_RULE_IDS
    .DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK]: DepositPaymentInstrumentLookbackState;
}

export interface ToggleState {
  readonly tier1: Tier1RuleState;
  readonly tier2: Tier2RuleState;
}

// ─── Result types ───────────────────────────────────────────────────

export type MapperErrorCode =
  | "invalid_number"
  | "non_positive_number"
  | "unsupported_period"
  | "missing_field";

export interface MapperError {
  readonly code: MapperErrorCode;
  readonly tier: "tier_1" | "tier_2";
  readonly ruleId: Tier1RuleId | Tier2RuleId;
  readonly field: string;
  readonly message: string;
}

export type SerializeResult =
  | { readonly ok: true; readonly body: PublishRuleGraphBody }
  | { readonly ok: false; readonly errors: readonly MapperError[] };

// ─── Terminal + structural node ids (canonical, exported) ───────────

export const NODE_IDS = {
  T1_SRC: "t1_src",
  T1_DECISION: "t1_decision",
  T2_SRC: "t2_src",
  T2_DECISION: "t2_decision",
  PROCESSED: "processed",
  AUTO_APPROVE: "auto_approve",
  HUMAN_REVIEW: "human_review",
} as const;

// ─── Small AST builders ─────────────────────────────────────────────

/**
 * Deterministic id builder. Composes `tier + kind + rule + suffix`
 * so the same toggle state always produces the same graph JSON.
 * Byte-stable output is critical for auditability and for diffing
 * versions in the UI.
 */
const astId = (
  tier: 1 | 2,
  kind: "c" | "f" | "l",
  rule: string,
  suffix?: string,
): string =>
  suffix ? `t${tier}_${kind}_${rule}_${suffix}` : `t${tier}_${kind}_${rule}`;

const featureRef = (
  tier: 1 | 2,
  rule: string,
  name: string,
  suffix?: string,
): FeatureRefNode => ({
  id: astId(tier, "f", rule, suffix),
  node_type: "feature_ref",
  name,
});

const literal = (
  tier: 1 | 2,
  rule: string,
  value: number | boolean | string,
  suffix?: string,
): LiteralNode => ({
  id: astId(tier, "l", rule, suffix),
  node_type: "literal",
  value,
});

const cmp = (
  tier: 1 | 2,
  rule: string,
  op: OperatorNode["op"],
  operands: readonly ConditionNode[],
  suffix?: string,
): OperatorNode => ({
  id: astId(tier, "c", rule, suffix),
  node_type: "operator",
  op,
  operands,
});

// ─── Field parsing ──────────────────────────────────────────────────

/**
 * Turns a string from a text input into a strictly positive integer.
 * Empty string, non-numeric, decimal, negative, and zero all produce
 * a `MapperError` — the BE features that consume these values
 * (thresholds, counts, day windows) don't have meaningful semantics
 * for 0 or fractional values.
 */
function parsePositiveInt(
  raw: string,
  tier: "tier_1" | "tier_2",
  ruleId: Tier1RuleId | Tier2RuleId,
  field: string,
): { readonly ok: true; readonly value: number } | { readonly ok: false; readonly error: MapperError } {
  if (raw === "" || raw == null) {
    return {
      ok: false,
      error: {
        code: "missing_field",
        tier,
        ruleId,
        field,
        message: `\`${field}\` is required`,
      },
    };
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    return {
      ok: false,
      error: {
        code: "invalid_number",
        tier,
        ruleId,
        field,
        message: `\`${field}\` must be a whole number`,
      },
    };
  }
  if (n <= 0) {
    return {
      ok: false,
      error: {
        code: "non_positive_number",
        tier,
        ruleId,
        field,
        message: `\`${field}\` must be greater than 0`,
      },
    };
  }
  return { ok: true, value: n };
}

function parsePeriodSuffix(
  period: PeriodValue,
  tier: "tier_1" | "tier_2",
  ruleId: Tier1RuleId | Tier2RuleId,
): { readonly ok: true; readonly suffix: "h" | "d" | "w" } | { readonly ok: false; readonly error: MapperError } {
  const suffix = PERIOD_TO_SUFFIX[period];
  if (!suffix) {
    return {
      ok: false,
      error: {
        code: "unsupported_period",
        tier,
        ruleId,
        field: "period",
        message: `Period \`${period}\` is not supported (must be HOUR, DAY, or WEEK)`,
      },
    };
  }
  return { ok: true, suffix };
}

// ─── Individual rule condition builders ─────────────────────────────
//
// Each builder returns either a single `ConditionNode` (which is what
// gets OR'd with siblings) or an array of errors that are collected
// across the whole tier. All builders are pure — no shared state.

type BuildOk = { readonly ok: true; readonly condition: ConditionNode };
type BuildErr = { readonly ok: false; readonly errors: readonly MapperError[] };
type BuildOutcome = BuildOk | BuildErr;

// ─── Tier 1 ────────────────────────────────────────────────────────

function buildMaxAmount(state: MaxAmountState): BuildOutcome {
  const parsed = parsePositiveInt(
    state.amount,
    "tier_1",
    TIER_1_RULE_IDS.MAX_AMOUNT,
    "amount",
  );
  if (!parsed.ok) return { ok: false, errors: [parsed.error] };
  // withdrawal_amount > <amount>
  return {
    ok: true,
    condition: cmp(1, "max_amount", ">", [
      featureRef(1, "max_amount", "withdrawal_amount"),
      literal(1, "max_amount", parsed.value),
    ]),
  };
}

function buildFirstTimeWithdrawal(): BuildOutcome {
  // Boolean flag — no numeric fields to validate, so this builder
  // can never fail. Kept as a builder (not inlined) for uniform call
  // sites in the tier assembler.
  return {
    ok: true,
    condition: cmp(1, "first_time_withdrawal", "==", [
      featureRef(1, "first_time_withdrawal", "is_first_time_withdrawal"),
      literal(1, "first_time_withdrawal", true),
    ]),
  };
}

function buildWithdrawalPerDay(state: WithdrawalPerDayState): BuildOutcome {
  const errors: MapperError[] = [];
  const threshold = parsePositiveInt(
    state.threshold,
    "tier_1",
    TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY,
    "threshold",
  );
  if (!threshold.ok) errors.push(threshold.error);
  const days = parsePositiveInt(
    state.days,
    "tier_1",
    TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY,
    "days",
  );
  if (!days.ok) errors.push(days.error);
  if (errors.length > 0) return { ok: false, errors };
  // Windowed feature: withdrawals_per_day_<n>d > <threshold>.
  // The BE spec fixes the suffix to `d` for `withdrawals_per_day`.
  return {
    ok: true,
    condition: cmp(1, "withdrawal_per_day", ">", [
      featureRef(
        1,
        "withdrawal_per_day",
        `withdrawals_per_day_${(days as { value: number }).value}d`,
      ),
      literal(1, "withdrawal_per_day", (threshold as { value: number }).value),
    ]),
  };
}

function buildWithdrawalsSinceDeposit(
  state: WithdrawalsSinceDepositState,
): BuildOutcome {
  const parsed = parsePositiveInt(
    state.withdrawals,
    "tier_1",
    TIER_1_RULE_IDS.WITHDRAWALS_SINCE_DEPOSIT,
    "withdrawals",
  );
  if (!parsed.ok) return { ok: false, errors: [parsed.error] };
  return {
    ok: true,
    condition: cmp(1, "withdrawals_since_deposit", ">", [
      featureRef(
        1,
        "withdrawals_since_deposit",
        "withdrawals_since_last_deposit",
      ),
      literal(1, "withdrawals_since_deposit", parsed.value),
    ]),
  };
}

function buildXTimesLastDeposit(state: XTimesLastDepositState): BuildOutcome {
  const parsed = parsePositiveInt(
    state.multiplier,
    "tier_1",
    TIER_1_RULE_IDS.X_TIMES_LAST_DEPOSIT,
    "multiplier",
  );
  if (!parsed.ok) return { ok: false, errors: [parsed.error] };
  return {
    ok: true,
    condition: cmp(1, "x_times_last_deposit", ">", [
      featureRef(
        1,
        "x_times_last_deposit",
        "current_withdrawal_to_last_deposit_ratio",
      ),
      literal(1, "x_times_last_deposit", parsed.value),
    ]),
  };
}

// ─── Tier 2 ────────────────────────────────────────────────────────

function buildReviewedXTimes(state: ReviewedXTimesState): BuildOutcome {
  const errors: MapperError[] = [];
  const reviews = parsePositiveInt(
    state.reviews,
    "tier_2",
    TIER_2_RULE_IDS.REVIEWED_X_TIMES,
    "reviews",
  );
  if (!reviews.ok) errors.push(reviews.error);
  const within = parsePositiveInt(
    state.within,
    "tier_2",
    TIER_2_RULE_IDS.REVIEWED_X_TIMES,
    "within",
  );
  if (!within.ok) errors.push(within.error);
  const suffix = parsePeriodSuffix(
    state.period,
    "tier_2",
    TIER_2_RULE_IDS.REVIEWED_X_TIMES,
  );
  if (!suffix.ok) errors.push(suffix.error);
  if (errors.length > 0) return { ok: false, errors };
  // prior_manual_review_count_<within><suffix> >= <reviews>
  return {
    ok: true,
    condition: cmp(2, "reviewed_x_times", ">=", [
      featureRef(
        2,
        "reviewed_x_times",
        `prior_manual_review_count_${(within as { value: number }).value}${(suffix as { suffix: string }).suffix}`,
      ),
      literal(2, "reviewed_x_times", (reviews as { value: number }).value),
    ]),
  };
}

function buildVipMatching(state: VipMatchingState): BuildOutcome {
  const errors: MapperError[] = [];
  const within = parsePositiveInt(
    state.within,
    "tier_2",
    TIER_2_RULE_IDS.VIP_MATCHING,
    "within",
  );
  if (!within.ok) errors.push(within.error);
  const suffix = parsePeriodSuffix(
    state.period,
    "tier_2",
    TIER_2_RULE_IDS.VIP_MATCHING,
  );
  if (!suffix.ok) errors.push(suffix.error);
  if (errors.length > 0) return { ok: false, errors };
  // Compound: is_vip == true AND same_instrument_success_count_<n><suffix> >= 1
  return {
    ok: true,
    condition: cmp(
      2,
      "vip_matching",
      "and",
      [
        cmp(
          2,
          "vip_matching",
          "==",
          [
            featureRef(2, "vip_matching", "is_vip", "vip"),
            literal(2, "vip_matching", true, "vip"),
          ],
          "vip",
        ),
        cmp(
          2,
          "vip_matching",
          ">=",
          [
            featureRef(
              2,
              "vip_matching",
              `same_instrument_success_count_${(within as { value: number }).value}${(suffix as { suffix: string }).suffix}`,
              "win",
            ),
            literal(2, "vip_matching", 1, "win"),
          ],
          "win",
        ),
      ],
    ),
  };
}

function buildSameInstrumentXTimes(
  state: SameInstrumentXTimesState,
): BuildOutcome {
  const errors: MapperError[] = [];
  const uses = parsePositiveInt(
    state.uses,
    "tier_2",
    TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES,
    "uses",
  );
  if (!uses.ok) errors.push(uses.error);
  const within = parsePositiveInt(
    state.within,
    "tier_2",
    TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES,
    "within",
  );
  if (!within.ok) errors.push(within.error);
  const suffix = parsePeriodSuffix(
    state.period,
    "tier_2",
    TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES,
  );
  if (!suffix.ok) errors.push(suffix.error);
  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    condition: cmp(2, "same_instrument_x_times", ">=", [
      featureRef(
        2,
        "same_instrument_x_times",
        `same_instrument_success_count_${(within as { value: number }).value}${(suffix as { suffix: string }).suffix}`,
      ),
      literal(
        2,
        "same_instrument_x_times",
        (uses as { value: number }).value,
      ),
    ]),
  };
}

function buildMinAccountAge(state: MinAccountAgeState): BuildOutcome {
  const parsed = parsePositiveInt(
    state.days,
    "tier_2",
    TIER_2_RULE_IDS.MIN_ACCOUNT_AGE,
    "days",
  );
  if (!parsed.ok) return { ok: false, errors: [parsed.error] };
  return {
    ok: true,
    condition: cmp(2, "min_account_age", ">=", [
      featureRef(2, "min_account_age", "account_age_days"),
      literal(2, "min_account_age", parsed.value),
    ]),
  };
}

function buildDepositPaymentInstrumentLookback(
  state: DepositPaymentInstrumentLookbackState,
): BuildOutcome {
  const errors: MapperError[] = [];
  const windowMatch = state.lookback.match(/^([1-9]\d*)([hdw])$/);
  if (!state.lookback) {
    errors.push({
      code: "missing_field",
      tier: "tier_2",
      ruleId: TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK,
      field: "lookback",
      message: "`lookback` is required",
    });
  } else if (!windowMatch) {
    errors.push({
      code: "unsupported_period",
      tier: "tier_2",
      ruleId: TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK,
      field: "lookback",
      message: "Lookback must use the `<n><h|d|w>` format",
    });
  }
  if (errors.length > 0) return { ok: false, errors };

  return {
    ok: true,
    condition: cmp(2, "deposit_payment_instrument_lookback", "==", [
      featureRef(
        2,
        "deposit_payment_instrument_lookback",
        `sole_deposit_payment_instrument_${state.lookback}`,
      ),
      literal(
        2,
        "deposit_payment_instrument_lookback",
        SPEI_PAYMENT_INSTRUMENT,
      ),
    ]),
  };
}

// ─── Tier assembly ──────────────────────────────────────────────────

/**
 * Collects the active rule conditions for one tier. Returns either
 * (a) the flat list of successfully-built conditions plus any
 * collected errors, or (b) an empty list if no rules are enabled.
 *
 * Note: an enabled-but-invalid rule contributes to `errors` and is
 * *not* silently dropped from the condition list — that would allow
 * a partial publish that doesn't match the user's stated intent.
 */
function buildTier1Conditions(tier1: Tier1RuleState): {
  readonly conditions: readonly ConditionNode[];
  readonly errors: readonly MapperError[];
} {
  const conditions: ConditionNode[] = [];
  const errors: MapperError[] = [];

  const consume = (outcome: BuildOutcome) => {
    if (outcome.ok) conditions.push(outcome.condition);
    else errors.push(...outcome.errors);
  };

  if (tier1[TIER_1_RULE_IDS.MAX_AMOUNT].enabled) {
    consume(buildMaxAmount(tier1[TIER_1_RULE_IDS.MAX_AMOUNT]));
  }
  if (tier1[TIER_1_RULE_IDS.FIRST_TIME_WITHDRAWAL].enabled) {
    consume(buildFirstTimeWithdrawal());
  }
  if (tier1[TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY].enabled) {
    consume(buildWithdrawalPerDay(tier1[TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY]));
  }
  if (tier1[TIER_1_RULE_IDS.WITHDRAWALS_SINCE_DEPOSIT].enabled) {
    consume(
      buildWithdrawalsSinceDeposit(
        tier1[TIER_1_RULE_IDS.WITHDRAWALS_SINCE_DEPOSIT],
      ),
    );
  }
  if (tier1[TIER_1_RULE_IDS.X_TIMES_LAST_DEPOSIT].enabled) {
    consume(
      buildXTimesLastDeposit(tier1[TIER_1_RULE_IDS.X_TIMES_LAST_DEPOSIT]),
    );
  }

  return { conditions, errors };
}

function buildTier2Conditions(tier2: Tier2RuleState): {
  readonly conditions: readonly ConditionNode[];
  readonly errors: readonly MapperError[];
} {
  const conditions: ConditionNode[] = [];
  const errors: MapperError[] = [];

  const consume = (outcome: BuildOutcome) => {
    if (outcome.ok) conditions.push(outcome.condition);
    else errors.push(...outcome.errors);
  };

  if (tier2[TIER_2_RULE_IDS.REVIEWED_X_TIMES].enabled) {
    consume(buildReviewedXTimes(tier2[TIER_2_RULE_IDS.REVIEWED_X_TIMES]));
  }
  if (tier2[TIER_2_RULE_IDS.VIP_MATCHING].enabled) {
    consume(buildVipMatching(tier2[TIER_2_RULE_IDS.VIP_MATCHING]));
  }
  if (tier2[TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES].enabled) {
    consume(
      buildSameInstrumentXTimes(
        tier2[TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES],
      ),
    );
  }
  if (tier2[TIER_2_RULE_IDS.MIN_ACCOUNT_AGE].enabled) {
    consume(buildMinAccountAge(tier2[TIER_2_RULE_IDS.MIN_ACCOUNT_AGE]));
  }
  if (tier2[TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK].enabled) {
    consume(
      buildDepositPaymentInstrumentLookback(
        tier2[TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK],
      ),
    );
  }

  return { conditions, errors };
}

/**
 * Combines N sibling conditions into a single node.
 *
 * - 0 conditions → `null` (caller writes an empty edges array).
 * - 1 condition  → returns it as-is (no `or` wrapper — keeps the
 *   graph minimal for the common single-rule case).
 * - N ≥ 2        → wraps in a single `or` operator.
 *
 * The `or` id includes the tier prefix so cross-tier ids never
 * collide.
 */
function combineWithOr(
  tier: 1 | 2,
  conditions: readonly ConditionNode[],
): ConditionNode | null {
  if (conditions.length === 0) return null;
  if (conditions.length === 1) return conditions[0];
  return {
    id: astId(tier, "c", "tier_or"),
    node_type: "operator",
    op: "or",
    operands: conditions,
  };
}

// ─── Public forward mapper ──────────────────────────────────────────

/**
 * Serialise the toggle-based UI state to a DAG body ready for
 * `POST /rules`. Result is either `{ ok: true, body }` or
 * `{ ok: false, errors }` — the caller (typically the RulesTab
 * component) inspects `.ok` to decide whether to enable the
 * Publish button.
 *
 * Guarantees on success (relied on by the reverse mapper):
 * - `body.source_node_id === "t1_src"`.
 * - `body.nodes` contains exactly 7 structural nodes: t1_src,
 *   t1_decision, t2_src, t2_decision, processed, auto_approve,
 *   human_review.
 * - The tier decisions have either 0 or 1 outgoing edges.
 * - Every id under `body.nodes` matches the deterministic id scheme
 *   documented above.
 *
 * Any deviation from these guarantees on the incoming BE graph is
 * exactly what the reverse mapper will refuse — that's the point
 * of pinning the forward output.
 */
export function toggleStateToGraph(state: ToggleState): SerializeResult {
  const tier1 = buildTier1Conditions(state.tier1);
  const tier2 = buildTier2Conditions(state.tier2);

  const errors = [...tier1.errors, ...tier2.errors];
  if (errors.length > 0) {
    return { ok: false, errors };
  }

  const t1Or = combineWithOr(1, tier1.conditions);
  const t2Or = combineWithOr(2, tier2.conditions);

  const t1Edges: readonly DecisionEdge[] = t1Or
    ? [{ target: NODE_IDS.T2_SRC, condition: t1Or }]
    : [];
  const t2Edges: readonly DecisionEdge[] = t2Or
    ? [{ target: NODE_IDS.AUTO_APPROVE, condition: t2Or }]
    : [];

  const t1Src: SourceNode = {
    id: NODE_IDS.T1_SRC,
    node_type: "source",
    tier: "tier_1",
    next_node_id: NODE_IDS.T1_DECISION,
  };
  const t1Decision: DecisionNode = {
    id: NODE_IDS.T1_DECISION,
    node_type: "decision",
    tier: "tier_1",
    // T1 default (no trigger fired) → `processed`, NOT `auto_approve`.
    // Splitting these terminals is what lets analytics tell "fell
    // through the risk gate" apart from "a T2 rule actively cleared
    // this". Operational effect is identical to auto_approve.
    default_target: NODE_IDS.PROCESSED,
    edges: t1Edges,
  };
  const t2Src: SourceNode = {
    id: NODE_IDS.T2_SRC,
    node_type: "source",
    tier: "tier_2",
    next_node_id: NODE_IDS.T2_DECISION,
  };
  const t2Decision: DecisionNode = {
    id: NODE_IDS.T2_DECISION,
    node_type: "decision",
    tier: "tier_2",
    default_target: NODE_IDS.HUMAN_REVIEW,
    edges: t2Edges,
  };
  const processed: TerminalNode = {
    id: NODE_IDS.PROCESSED,
    node_type: "terminal",
    decision: "processed",
  };
  const autoApprove: TerminalNode = {
    id: NODE_IDS.AUTO_APPROVE,
    node_type: "terminal",
    decision: "auto_approve",
  };
  const humanReview: TerminalNode = {
    id: NODE_IDS.HUMAN_REVIEW,
    node_type: "terminal",
    decision: "human_review",
  };

  // Explicit `Record<string, DagNode>` cast — TS can't infer the
  // widened index signature from an object literal of narrower node
  // types. The runtime value is identical.
  const nodes: Record<string, DagNode> = {
    [t1Src.id]: t1Src,
    [t1Decision.id]: t1Decision,
    [t2Src.id]: t2Src,
    [t2Decision.id]: t2Decision,
    [processed.id]: processed,
    [autoApprove.id]: autoApprove,
    [humanReview.id]: humanReview,
  };

  return {
    ok: true,
    body: {
      source_node_id: NODE_IDS.T1_SRC,
      nodes,
    },
  };
}

// ─── Reverse mapper ────────────────────────────────────────────────
//
// The parser accepts graphs shaped exactly like the forward mapper's
// output. It's deliberately structural (checks node ids + shape),
// but *semantic* on the AST layer (matches by feature name + op,
// not by AST node id). That split means:
//
//  - A round-trip forward → reverse is guaranteed to yield the
//    original state, byte-for-byte.
//  - A graph published by a future editor or by a BE with slightly
//    different AST ids can still be recognised, as long as the
//    semantics match.
//
// Anything else returns `ok: false` and the RulesTab enters
// read-only mode. That failure mode is intentional — quietly
// accepting an "almost right" graph would let the toggle UI mislead
// the admin about what's actually running.

export type DeserializeReason =
  | "unrecognized_structure"
  | "unknown_feature"
  | "unexpected_operator"
  | "unexpected_literal";

export type DeserializeResult =
  | { readonly ok: true; readonly state: ToggleState }
  | {
      readonly ok: false;
      readonly reason: DeserializeReason;
      readonly detail: string;
    };

const fail = (
  reason: DeserializeReason,
  detail: string,
): { readonly ok: false; readonly reason: DeserializeReason; readonly detail: string } =>
  ({ ok: false, reason, detail });

/** Type-narrowing predicates used repeatedly in the parser. */
const isOperator = (n: ConditionNode): n is OperatorNode =>
  n.node_type === "operator";
const isFeatureRef = (n: ConditionNode): n is FeatureRefNode =>
  n.node_type === "feature_ref";
const isLiteral = (n: ConditionNode): n is LiteralNode =>
  n.node_type === "literal";

/**
 * Matches a two-operand comparison whose left operand is a
 * `feature_ref` with the exact expected name and whose right operand
 * is a numeric literal. Returns the extracted number, or null when
 * the shape doesn't match.
 *
 * The `nameOrPredicate` param is either a fixed string (for
 * single-value features like `withdrawal_amount`) or a regex whose
 * capture groups the caller consumes (for windowed features like
 * `withdrawals_per_day_(\d+)d`). Splitting the two cases would
 * duplicate ~10 lines of narrowing per matcher — one function is
 * cleaner.
 */
function matchNumberCompare(
  node: ConditionNode,
  op: OperatorNode["op"],
  nameOrPredicate: string | RegExp,
): { readonly value: number; readonly match: RegExpMatchArray | null } | null {
  if (!isOperator(node) || node.op !== op) return null;
  if (node.operands.length !== 2) return null;
  const [left, right] = node.operands;
  if (!isFeatureRef(left) || !isLiteral(right)) return null;
  if (typeof right.value !== "number") return null;
  if (typeof nameOrPredicate === "string") {
    if (left.name !== nameOrPredicate) return null;
    return { value: right.value, match: null };
  }
  const m = left.name.match(nameOrPredicate);
  if (!m) return null;
  return { value: right.value, match: m };
}

/** Suffix (`h`/`d`/`w`) back to the UI's `PeriodValue`. */
function suffixToPeriod(suffix: string): PeriodValue | null {
  if (suffix === "h") return "HOUR";
  if (suffix === "d") return "DAY";
  if (suffix === "w") return "WEEK";
  return null;
}

// ─── Rule matchers ─────────────────────────────────────────────────
//
// Each matcher takes a `ConditionNode` and returns either the parsed
// state fields (with the rule id) or null if the shape doesn't match.
// Order matters only for shape-overlapping matchers (there aren't
// any today — every matcher pins to a different feature name — but
// the pattern is future-proofed by iterating a tier-scoped array).

type Tier1Match =
  | {
      readonly ruleId: typeof TIER_1_RULE_IDS.MAX_AMOUNT;
      readonly patch: MaxAmountState;
    }
  | {
      readonly ruleId: typeof TIER_1_RULE_IDS.FIRST_TIME_WITHDRAWAL;
      readonly patch: FirstTimeWithdrawalState;
    }
  | {
      readonly ruleId: typeof TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY;
      readonly patch: WithdrawalPerDayState;
    }
  | {
      readonly ruleId: typeof TIER_1_RULE_IDS.WITHDRAWALS_SINCE_DEPOSIT;
      readonly patch: WithdrawalsSinceDepositState;
    }
  | {
      readonly ruleId: typeof TIER_1_RULE_IDS.X_TIMES_LAST_DEPOSIT;
      readonly patch: XTimesLastDepositState;
    };

type Tier2Match =
  | {
      readonly ruleId: typeof TIER_2_RULE_IDS.MIN_ACCOUNT_AGE;
      readonly patch: MinAccountAgeState;
    }
  | {
      readonly ruleId: typeof TIER_2_RULE_IDS.REVIEWED_X_TIMES;
      readonly patch: ReviewedXTimesState;
    }
  | {
      readonly ruleId: typeof TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES;
      readonly patch: SameInstrumentXTimesState;
    }
  | {
      readonly ruleId: typeof TIER_2_RULE_IDS.VIP_MATCHING;
      readonly patch: VipMatchingState;
    }
  | {
      readonly ruleId: typeof TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK;
      readonly patch: DepositPaymentInstrumentLookbackState;
    };

const TIER_1_MATCHERS: readonly ((node: ConditionNode) => Tier1Match | null)[] = [
  (node) => {
    const m = matchNumberCompare(node, ">", "withdrawal_amount");
    return m
      ? {
          ruleId: TIER_1_RULE_IDS.MAX_AMOUNT,
          patch: { enabled: true, amount: String(m.value) },
        }
      : null;
  },
  (node) => {
    if (
      !isOperator(node) ||
      node.op !== "==" ||
      node.operands.length !== 2
    )
      return null;
    const [left, right] = node.operands;
    if (
      !isFeatureRef(left) ||
      left.name !== "is_first_time_withdrawal" ||
      !isLiteral(right) ||
      right.value !== true
    )
      return null;
    return {
      ruleId: TIER_1_RULE_IDS.FIRST_TIME_WITHDRAWAL,
      patch: { enabled: true },
    };
  },
  (node) => {
    const m = matchNumberCompare(node, ">", /^withdrawals_per_day_(\d+)d$/);
    if (!m || !m.match) return null;
    return {
      ruleId: TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY,
      patch: {
        enabled: true,
        threshold: String(m.value),
        days: m.match[1],
      },
    };
  },
  (node) => {
    const m = matchNumberCompare(node, ">", "withdrawals_since_last_deposit");
    return m
      ? {
          ruleId: TIER_1_RULE_IDS.WITHDRAWALS_SINCE_DEPOSIT,
          patch: { enabled: true, withdrawals: String(m.value) },
        }
      : null;
  },
  (node) => {
    // Prefer the current BE feature name; keep the legacy name so
    // graphs saved before the rename still deserialize into the UI.
    const m =
      matchNumberCompare(
        node,
        ">",
        "current_withdrawal_to_last_deposit_ratio",
      ) ?? matchNumberCompare(node, ">", "withdrawal_to_last_deposit_ratio");
    return m
      ? {
          ruleId: TIER_1_RULE_IDS.X_TIMES_LAST_DEPOSIT,
          patch: { enabled: true, multiplier: String(m.value) },
        }
      : null;
  },
];

/** Recognise `is_vip == true AND same_instrument_success_count_<n><h|d|w> >= 1`. */
function matchVipMatching(node: ConditionNode): Tier2Match | null {
  if (!isOperator(node) || node.op !== "and" || node.operands.length !== 2)
    return null;
  const [a, b] = node.operands;

  // The two conjuncts can appear in either order. Try both.
  const orderings: readonly [ConditionNode, ConditionNode][] = [
    [a, b],
    [b, a],
  ];
  for (const [vipNode, winNode] of orderings) {
    // is_vip == true
    if (
      !isOperator(vipNode) ||
      vipNode.op !== "==" ||
      vipNode.operands.length !== 2
    )
      continue;
    const [vipLeft, vipRight] = vipNode.operands;
    if (
      !isFeatureRef(vipLeft) ||
      vipLeft.name !== "is_vip" ||
      !isLiteral(vipRight) ||
      vipRight.value !== true
    )
      continue;

    // same_instrument_success_count_<n><h|d|w> >= 1
    const win = matchNumberCompare(
      winNode,
      ">=",
      /^same_instrument_success_count_(\d+)([hdw])$/,
    );
    if (!win || !win.match || win.value !== 1) continue;
    const period = suffixToPeriod(win.match[2]);
    if (!period) continue;

    return {
      ruleId: TIER_2_RULE_IDS.VIP_MATCHING,
      patch: {
        enabled: true,
        within: win.match[1],
        period,
      },
    };
  }
  return null;
}

const TIER_2_MATCHERS: readonly ((node: ConditionNode) => Tier2Match | null)[] = [
  (node) => {
    const m = matchNumberCompare(node, ">=", "account_age_days");
    return m
      ? {
          ruleId: TIER_2_RULE_IDS.MIN_ACCOUNT_AGE,
          patch: { enabled: true, days: String(m.value) },
        }
      : null;
  },
  (node) => {
    const m = matchNumberCompare(
      node,
      ">=",
      /^prior_manual_review_count_(\d+)([hdw])$/,
    );
    if (!m || !m.match) return null;
    const period = suffixToPeriod(m.match[2]);
    if (!period) return null;
    return {
      ruleId: TIER_2_RULE_IDS.REVIEWED_X_TIMES,
      patch: {
        enabled: true,
        reviews: String(m.value),
        within: m.match[1],
        period,
      },
    };
  },
  // VIP_MATCHING must be tried BEFORE SAME_INSTRUMENT_X_TIMES: the
  // former is wrapped in an `and` so it wouldn't match the latter,
  // but this comment locks the reader's intuition — a future dev
  // could think the order is arbitrary.
  matchVipMatching,
  (node) => {
    const m = matchNumberCompare(
      node,
      ">=",
      /^same_instrument_success_count_(\d+)([hdw])$/,
    );
    if (!m || !m.match) return null;
    const period = suffixToPeriod(m.match[2]);
    if (!period) return null;
    return {
      ruleId: TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES,
      patch: {
        enabled: true,
        uses: String(m.value),
        within: m.match[1],
        period,
      },
    };
  },
  (node) => {
    if (
      !isOperator(node) ||
      node.op !== "==" ||
      node.operands.length !== 2
    ) {
      return null;
    }
    const [left, right] = node.operands;
    if (
      !isFeatureRef(left) ||
      !isLiteral(right) ||
      typeof right.value !== "string"
    ) {
      return null;
    }
    const featureMatch = left.name.match(
      /^sole_deposit_payment_instrument_([1-9]\d*[hdw])$/,
    );
    if (!featureMatch || right.value !== SPEI_PAYMENT_INSTRUMENT) return null;
    return {
      ruleId: TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK,
      patch: {
        enabled: true,
        lookback: featureMatch[1],
      },
    };
  },
];

// ─── Structural validation ─────────────────────────────────────────

/**
 * Peel the possibly-`or`-wrapped tier condition into a flat list of
 * per-rule conditions. Matches the forward mapper's `combineWithOr`:
 * a single rule stays as-is, ≥2 rules land under one `or`, an empty
 * tier passes `null` in.
 */
function flattenTierCondition(
  cond: ConditionNode | null,
): readonly ConditionNode[] | null {
  if (cond === null) return [];
  if (isOperator(cond) && cond.op === "or") {
    // Reject nested-or (forward mapper only ever emits a flat OR).
    // Anything else is a structure the toggle UI can't represent.
    if (cond.operands.some((o) => isOperator(o) && o.op === "or"))
      return null;
    return cond.operands;
  }
  return [cond];
}

/**
 * State scaffold with every rule disabled. Used internally by the
 * reverse mapper to build up the result, and exported so RulesTab
 * can render a "nothing published yet" empty state that matches the
 * shape the reverse mapper produces.
 *
 * Callers who want the historical "everything on with blank inputs"
 * defaults should NOT use this — that's a legacy pre-persistence UX
 * that would make every rule fail validation on first paint.
 */
export function emptyState(): ToggleState {
  return {
    tier1: {
      [TIER_1_RULE_IDS.MAX_AMOUNT]: { enabled: false, amount: "" },
      [TIER_1_RULE_IDS.FIRST_TIME_WITHDRAWAL]: { enabled: false },
      [TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY]: {
        enabled: false,
        threshold: "",
        days: "",
      },
      [TIER_1_RULE_IDS.WITHDRAWALS_SINCE_DEPOSIT]: {
        enabled: false,
        withdrawals: "",
      },
      [TIER_1_RULE_IDS.X_TIMES_LAST_DEPOSIT]: {
        enabled: false,
        multiplier: "",
      },
    },
    tier2: {
      [TIER_2_RULE_IDS.REVIEWED_X_TIMES]: {
        enabled: false,
        reviews: "",
        within: "",
        period: "DAY",
      },
      [TIER_2_RULE_IDS.VIP_MATCHING]: {
        enabled: false,
        within: "",
        period: "DAY",
      },
      [TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES]: {
        enabled: false,
        uses: "",
        within: "",
        period: "DAY",
      },
      [TIER_2_RULE_IDS.MIN_ACCOUNT_AGE]: { enabled: false, days: "" },
      [TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK]: {
        enabled: false,
        lookback: "",
      },
    },
  };
}

export function graphToToggleState(graph: RuleGraph): DeserializeResult {
  const { source_node_id, nodes } = graph;

  // ─── Structural checks ──────────────────────────────────────────

  if (source_node_id !== NODE_IDS.T1_SRC) {
    return fail(
      "unrecognized_structure",
      `Expected source_node_id \`${NODE_IDS.T1_SRC}\`, got \`${source_node_id}\``,
    );
  }

  // `readonly` array (not Set) because the tsconfig target is ES5
  // and iterating a Set requires downlevelIteration — the array
  // fits our 7-element case perfectly and reads cleaner anyway.
  const expectedIds: readonly string[] = [
    NODE_IDS.T1_SRC,
    NODE_IDS.T1_DECISION,
    NODE_IDS.T2_SRC,
    NODE_IDS.T2_DECISION,
    NODE_IDS.PROCESSED,
    NODE_IDS.AUTO_APPROVE,
    NODE_IDS.HUMAN_REVIEW,
  ];
  const actualIds = Object.keys(nodes);
  if (
    actualIds.length !== expectedIds.length ||
    expectedIds.some((id) => !Object.prototype.hasOwnProperty.call(nodes, id))
  ) {
    return fail(
      "unrecognized_structure",
      `Node id set differs from the canonical 7 (expected exactly ${expectedIds.join(", ")}, got ${actualIds.join(", ")})`,
    );
  }

  const t1Src = nodes[NODE_IDS.T1_SRC];
  const t1Decision = nodes[NODE_IDS.T1_DECISION];
  const t2Src = nodes[NODE_IDS.T2_SRC];
  const t2Decision = nodes[NODE_IDS.T2_DECISION];
  const processed = nodes[NODE_IDS.PROCESSED];
  const autoApprove = nodes[NODE_IDS.AUTO_APPROVE];
  const humanReview = nodes[NODE_IDS.HUMAN_REVIEW];

  if (
    t1Src.node_type !== "source" ||
    t1Src.next_node_id !== NODE_IDS.T1_DECISION
  ) {
    return fail("unrecognized_structure", "t1_src is not a properly wired source");
  }
  if (
    t2Src.node_type !== "source" ||
    t2Src.next_node_id !== NODE_IDS.T2_DECISION
  ) {
    return fail("unrecognized_structure", "t2_src is not a properly wired source");
  }
  if (
    processed.node_type !== "terminal" ||
    processed.decision !== "processed"
  ) {
    return fail("unrecognized_structure", "processed terminal has wrong decision type");
  }
  if (
    autoApprove.node_type !== "terminal" ||
    autoApprove.decision !== "auto_approve"
  ) {
    return fail("unrecognized_structure", "auto_approve terminal has wrong decision type");
  }
  if (
    humanReview.node_type !== "terminal" ||
    humanReview.decision !== "human_review"
  ) {
    return fail("unrecognized_structure", "human_review terminal has wrong decision type");
  }

  if (t1Decision.node_type !== "decision") {
    return fail("unrecognized_structure", "t1_decision is not a decision node");
  }
  if (t2Decision.node_type !== "decision") {
    return fail("unrecognized_structure", "t2_decision is not a decision node");
  }
  if (t1Decision.default_target !== NODE_IDS.PROCESSED) {
    return fail(
      "unrecognized_structure",
      `t1_decision.default_target must be \`${NODE_IDS.PROCESSED}\`, got \`${t1Decision.default_target}\``,
    );
  }
  if (t2Decision.default_target !== NODE_IDS.HUMAN_REVIEW) {
    return fail(
      "unrecognized_structure",
      `t2_decision.default_target must be \`${NODE_IDS.HUMAN_REVIEW}\`, got \`${t2Decision.default_target}\``,
    );
  }
  if (t1Decision.edges.length > 1) {
    return fail(
      "unrecognized_structure",
      `t1_decision must have 0 or 1 outgoing edges (got ${t1Decision.edges.length})`,
    );
  }
  if (t2Decision.edges.length > 1) {
    return fail(
      "unrecognized_structure",
      `t2_decision must have 0 or 1 outgoing edges (got ${t2Decision.edges.length})`,
    );
  }
  if (t1Decision.edges[0] && t1Decision.edges[0].target !== NODE_IDS.T2_SRC) {
    return fail(
      "unrecognized_structure",
      `t1_decision edge target must be \`${NODE_IDS.T2_SRC}\`, got \`${t1Decision.edges[0].target}\``,
    );
  }
  if (
    t2Decision.edges[0] &&
    t2Decision.edges[0].target !== NODE_IDS.AUTO_APPROVE
  ) {
    return fail(
      "unrecognized_structure",
      `t2_decision edge target must be \`${NODE_IDS.AUTO_APPROVE}\`, got \`${t2Decision.edges[0].target}\``,
    );
  }

  // ─── Extract per-rule conditions ────────────────────────────────

  const t1Conds = flattenTierCondition(t1Decision.edges[0]?.condition ?? null);
  if (t1Conds === null) {
    return fail(
      "unrecognized_structure",
      "Tier 1 condition contains a nested OR (only flat OR is supported)",
    );
  }
  const t2Conds = flattenTierCondition(t2Decision.edges[0]?.condition ?? null);
  if (t2Conds === null) {
    return fail(
      "unrecognized_structure",
      "Tier 2 condition contains a nested OR (only flat OR is supported)",
    );
  }

  // ─── Match each rule condition against known patterns ───────────

  // Cast through `unknown` because `ToggleState.tier1` is a strict
  // per-key type, not an index-signed record — we want the loose
  // record shape only for the local mutation below, then narrow
  // back at the return.
  const state = emptyState() as unknown as {
    tier1: Record<string, unknown>;
    tier2: Record<string, unknown>;
  };

  for (const cond of t1Conds) {
    let matched: Tier1Match | null = null;
    for (const matcher of TIER_1_MATCHERS) {
      matched = matcher(cond);
      if (matched) break;
    }
    if (!matched) {
      return fail(
        "unknown_feature",
        `Tier 1 condition did not match any known rule pattern`,
      );
    }
    // A rule appearing twice in the same OR is nonsense and something
    // the toggle UI would never emit — reject.
    if (
      (state.tier1[matched.ruleId] as { enabled: boolean }).enabled === true
    ) {
      return fail(
        "unrecognized_structure",
        `Tier 1 rule \`${matched.ruleId}\` appears more than once`,
      );
    }
    state.tier1[matched.ruleId] = matched.patch;
  }

  for (const cond of t2Conds) {
    let matched: Tier2Match | null = null;
    for (const matcher of TIER_2_MATCHERS) {
      matched = matcher(cond);
      if (matched) break;
    }
    if (!matched) {
      return fail(
        "unknown_feature",
        `Tier 2 condition did not match any known rule pattern`,
      );
    }
    if (
      (state.tier2[matched.ruleId] as { enabled: boolean }).enabled === true
    ) {
      return fail(
        "unrecognized_structure",
        `Tier 2 rule \`${matched.ruleId}\` appears more than once`,
      );
    }
    state.tier2[matched.ruleId] = matched.patch;
  }

  return { ok: true, state: state as unknown as ToggleState };
}
